/**
 * Service worker registration — done by hand for one reason: `updateViaCache`.
 *
 * vite-plugin-pwa's generated `registerSW.js` calls
 * `register('/sw.js', { scope: '/' })` with no cache policy, which leaves the
 * browser free to answer the worker's update check out of the HTTP cache.
 *
 * That is not theoretical here. `public/_headers` sets `Cache-Control: no-cache`
 * on `/sw.js` and Pages serves it correctly — verified directly against the
 * origin, `floorfile-web-staging.pages.dev` returns `no-cache` — but the same
 * file requested through the zone (`staging`/`app.floorfile.com`) comes back
 * `max-age=14400`. The zone overrides the origin header, and that setting is not
 * in this repo.
 *
 * A four-hour-stale worker script is exactly the window that produced the
 * "every page is broken" report: `autoUpdate` ships `skipWaiting` +
 * `clientsClaim` + `cleanupOutdatedCaches`, so once the new worker does land it
 * deletes the precache out from under tabs still running the old build, and
 * their remaining lazy routes exist in neither the cache nor the server.
 *
 * `updateViaCache: 'none'` takes the HTTP cache out of the update path
 * entirely, so update detection no longer depends on a CDN setting this repo
 * does not control. `lib/lazy-route.ts` remains the recovery path for tabs that
 * are already stranded; this narrows how many ever get there.
 */
export function registerServiceWorker(): void {
  // `sw.js` is emitted by the production build only — there is no worker to
  // register under `vite dev`, and attempting it logs a spurious 404.
  if (!import.meta.env.PROD) return
  if (!('serviceWorker' in navigator)) return

  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' }).catch(() => {
      // A failed registration costs offline support, not the app: the shell and
      // every route work without a worker. Never let it break first paint.
    })
  })
}
